import { useState } from "react"
import { createFileRoute } from "@tanstack/react-router"
import { useQuery } from "@tanstack/react-query"
import { api } from "@/lib/api"
import { auth } from "@/lib/auth"
import { useT } from "@/lib/i18n"
import { Badge, Button, Card, CardBody, CardHeader, Spinner, Toast } from "@/components/ui"
import type { BillingCheckoutOut, BillingInterval, MyUsageOut, PlansOut } from "@/lib/types"
import { ExternalLinkIcon } from "lucide-react"
import { PlanCards } from "@/components/PlanCards"
import { IntervalToggle } from "@/components/Pricing"

export const Route = createFileRoute("/_app/billing")({
  component: BillingPage,
})

function BillingPage() {
  const t = useT()
  const me = auth.me()
  const [busy, setBusy] = useState<"checkout" | "portal" | null>(null)
  const [interval, setInterval] = useState<BillingInterval>("month")
  const [err, setErr] = useState<string | null>(null)
  const plans = useQuery({ queryKey: ["plans"], queryFn: () => api<PlansOut>("/api/billing/plans") })
  const usage = useQuery({
    queryKey: ["my-usage", "30d"],
    queryFn: () => api<MyUsageOut>("/api/me/usage", { query: { period: "30d" } }),
  })

  async function upgrade(target: "pro" | "team" = "pro", iv: BillingInterval = interval) {
    setBusy("checkout"); setErr(null)
    try {
      const out = await api<BillingCheckoutOut>("/api/billing/checkout", { method: "POST", query: { tier: target, interval: iv } })
      window.location.href = out.url
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(null)
    }
  }

  async function portal() {
    setBusy("portal"); setErr(null)
    try {
      const out = await api<BillingCheckoutOut>("/api/billing/portal", { method: "POST" })
      window.location.href = out.url
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(null)
    }
  }

  const tier = me?.tier
  const isPaid = tier === "pro" || tier === "team"
  const proPrice = plans.data?.plans.find((p) => p.tier === "pro")?.price_eur_month ?? 19
  const tierKey = `bill.tier.${tier ?? ""}`
  const tierLabel = tier && t(tierKey) !== tierKey ? t(tierKey) : tier

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">{t("bill.title")}</h1>
        <p className="text-[13px] text-muted-foreground mt-1.5">{t("bill.sub")}</p>
      </div>

      {err && <Toast variant="destructive">{err}</Toast>}

      <Card>
        <CardHeader title={t("bill.currentPlan")} right={<Badge variant={isPaid ? "ok" : "muted"}>{tierLabel}</Badge>} />
        <CardBody className="space-y-3">
          {tier === "free" && (
            <>
              <p className="text-sm text-muted-foreground">{t("bill.freeDesc")}</p>
              <Button onClick={() => upgrade("pro")} disabled={busy === "checkout"}>
                {busy === "checkout" ? <Spinner /> : null} {t("bill.upgrade", { price: proPrice })}
              </Button>
            </>
          )}
          {isPaid && (
            <>
              <p className="text-sm text-muted-foreground">{t("bill.paidDesc")}</p>
              <Button variant="outline" onClick={portal} disabled={busy === "portal"}>
                {busy === "portal" ? <Spinner /> : <ExternalLinkIcon className="h-3 w-3" />} {t("bill.openPortal")}
              </Button>
            </>
          )}
          {tier === "admin" && (
            <p className="text-sm text-muted-foreground">{t("bill.adminDesc")}</p>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title={t("bill.plans")} subtitle={t("bill.plansSub")}
          right={tier === "free" ? <IntervalToggle value={interval} onChange={setInterval} /> : undefined} />
        <CardBody>
          {plans.isLoading && <Spinner />}
          {plans.data && (
            <PlanCards plans={plans.data.plans} current={tier} interval={interval} busy={busy === "checkout"}
              proNote={tier === "free" && (plans.data.trial_days ?? 0) > 0 ? t("pricing.trialRibbon", { days: plans.data.trial_days ?? 0 }) : undefined}
              onChoose={tier === "free" ? (target, iv) => upgrade(target, iv) : undefined} />
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title={t("bill.spend30")} subtitle={t("bill.spend30Sub")} />
        <CardBody>
          {usage.isLoading && <Spinner />}
          {usage.data && (
            <div className="space-y-1">
              <div className="text-3xl font-semibold tabular-nums">
                ${usage.data.split.total_usd.toFixed(2)}
                <span className="text-xs text-muted-foreground ml-2 font-normal">{t("bill.last30")}</span>
              </div>
              {usage.data.split.self_usd > 0 && (
                <div className="text-[10px] text-muted-foreground">
                  {t("bill.split", { platform: usage.data.split.platform_usd.toFixed(2), self: usage.data.split.self_usd.toFixed(2) })}
                </div>
              )}
            </div>
          )}
          {usage.error && <Toast variant="destructive">{(usage.error as Error).message}</Toast>}
        </CardBody>
      </Card>
    </div>
  )
}
